/**
 * ChangePasswordRequiredPage — /change-password-required.
 *
 * Forced interstitial rendered by AppShell whenever the session user
 * has `mustChangePassword=true` on /api/v1/auth/me. The user is signed
 * in (otherwise they'd be on /login) but is blocked from every other
 * surface until they pick a new password.
 *
 * UX choices:
 *   - No nav, no sidebar — the page deliberately looks like login,
 *     calm and single-purpose, so the user doesn't feel trapped in the
 *     app chrome.
 *   - Only one action: change the password. The sign-out shortcut is
 *     available so a user who suspects they're being phished can bail.
 *   - On success, refetches /me — the BE clears the flag, the guard in
 *     AppShell unblocks, and the user lands wherever they were headed.
 */

import { useId, useState, type FormEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { ApiError } from '../api/client'
import { changeOwnPassword } from '../api/auth-password'
import { startLogout, useSession } from '../auth/auth-context'
import { Button } from '../components/primitives/Button'

const MIN_NEW_PASSWORD_LENGTH = 8

function getErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return fallback
}

export function ChangePasswordRequiredPage(): JSX.Element {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { user } = useSession()
  const currentId = useId()
  const newId = useId()
  const confirmId = useId()

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault()
    setError(null)

    if (newPassword.length < MIN_NEW_PASSWORD_LENGTH) {
      setError(`New password must be at least ${MIN_NEW_PASSWORD_LENGTH} characters.`)
      return
    }
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.')
      return
    }
    if (newPassword === currentPassword) {
      setError('New password must differ from the current password.')
      return
    }

    setSubmitting(true)
    try {
      await changeOwnPassword({ currentPassword, newPassword })
      // Refetch the session FIRST so the flag is cleared in cache, THEN
      // navigate home. This page renders OUTSIDE AppShell, so its guard
      // isn't mounted to release us — we must navigate ourselves. Awaiting
      // the invalidation guarantees AppShell (mounting at '/') reads
      // mustChangePassword=false and doesn't bounce us straight back here.
      await queryClient.invalidateQueries({ queryKey: ['session', 'me'] })
      await navigate({ to: '/' })
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to change password.'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <main
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--paper-sunken)',
        padding: 16,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          background: 'var(--paper-raised)',
          border: '1px solid var(--line)',
          borderRadius: 10,
          padding: '28px 28px 24px',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        <h1
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 20,
            fontWeight: 400,
            color: 'var(--ink)',
            margin: '0 0 6px',
          }}
        >
          Set a new password
        </h1>
        <p
          style={{
            fontSize: 13,
            color: 'var(--ink-2)',
            lineHeight: 1.55,
            margin: '0 0 20px',
          }}
        >
          {user?.name ? `${user.name}, ` : ''}your password must be rotated
          before you continue. Choose a new one to unlock the app.
        </p>

        <form onSubmit={(e) => void handleSubmit(e)} noValidate>
          <Field
            id={currentId}
            label="Current password"
            value={currentPassword}
            onChange={setCurrentPassword}
            autoComplete="current-password"
            autoFocus
          />
          <Field
            id={newId}
            label="New password"
            value={newPassword}
            onChange={setNewPassword}
            autoComplete="new-password"
            hint={`At least ${MIN_NEW_PASSWORD_LENGTH} characters.`}
          />
          <Field
            id={confirmId}
            label="Confirm new password"
            value={confirmPassword}
            onChange={setConfirmPassword}
            autoComplete="new-password"
          />

          {error ? (
            <div
              role="alert"
              style={{
                marginTop: 10,
                padding: '10px 12px',
                borderRadius: 6,
                background: 'var(--danger-soft)',
                color: 'var(--danger)',
                fontSize: 13,
              }}
            >
              {error}
            </div>
          ) : null}

          <div
            style={{
              marginTop: 20,
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? 'Saving…' : 'Update password'}
            </Button>
            <button
              type="button"
              onClick={() => {
                void startLogout()
              }}
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: 13,
                color: 'var(--ink-3)',
                cursor: 'pointer',
                padding: 8,
              }}
            >
              Sign out instead
            </button>
          </div>
        </form>
      </div>
    </main>
  )
}

interface FieldProps {
  id: string
  label: string
  value: string
  onChange: (next: string) => void
  autoComplete: string
  hint?: string
  autoFocus?: boolean
}

function Field({
  id,
  label,
  value,
  onChange,
  autoComplete,
  hint,
  autoFocus,
}: Readonly<FieldProps>): JSX.Element {
  return (
    <div style={{ marginBottom: 14 }}>
      <label
        htmlFor={id}
        style={{
          display: 'block',
          fontFamily: 'var(--font-mono)',
          fontSize: 10,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: 'var(--ink-3)',
          marginBottom: 4,
        }}
      >
        {label}
      </label>
      <input
        id={id}
        type="password"
        value={value}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          minHeight: 44,
          padding: '8px 12px',
          fontSize: 14,
          fontFamily: 'var(--font-mono)',
          color: 'var(--ink)',
          background: 'var(--paper)',
          border: '1px solid var(--line)',
          borderRadius: 6,
          outline: 'none',
        }}
      />
      {hint ? (
        <div
          style={{
            marginTop: 4,
            fontSize: 11,
            color: 'var(--ink-3)',
            fontFamily: 'var(--font-sans)',
          }}
        >
          {hint}
        </div>
      ) : null}
    </div>
  )
}
